import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from "react-router-dom";
import { Chat } from '@progress/kendo-react-conversational-ui';
import { SpeechToTextResultEvent } from '@progress/kendo-react-buttons';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';

import { PromptBox } from './PromptBox';
import { AIMessageTemplate } from './chat';
import { useNucliaChat } from '../../../hooks';
import { DOCUMENT_OPTIONS, getDefaultDocument } from '../../../constants';
import { DocumentOption } from '../../../types';

import '../../auth/components/Auth.css';

interface NavigationState {
    doc?: { value: DocumentOption };
    query?: string;
    isInternetSearchEnabled?: boolean;
    isInternalEnabled?: boolean;
}

export const PepsiCoRagChat: React.FC = () => {
    const { state } = useLocation() as { state: NavigationState | null };
    const hasInitialQueryRun = useRef(false);

    const { messages, isLoading, isServiceReady, serviceError, askQuestion } = useNucliaChat();

    const [query, setQuery] = useState("");
    const [ddlValue, setDdlValue] = useState<{ value: DocumentOption }>({
        value: state?.doc?.value || getDefaultDocument()
    });
    const [ISEnabled, setISEnabled] = useState<boolean>(
        state?.isInternetSearchEnabled ?? false
    );
    const [IEnabled, setIEnabled] = useState<boolean>(
        state?.isInternalEnabled ?? true
    );

    useEffect(() => {
        if (hasInitialQueryRun.current) return;
        if (!isServiceReady) return;

        if (state?.query && state.query.trim()) {
            hasInitialQueryRun.current = true;
            const documentId = state.doc?.value?.id || getDefaultDocument().id;
            const enableInternetSearch = state.isInternetSearchEnabled ?? false;
            const enableRestrictedInternal = state.isInternalEnabled ?? true;

            askQuestion(state.query, documentId, enableInternetSearch, enableRestrictedInternal);

            // Clear the state after consuming it to prevent re-sending on refresh
            window.history.replaceState({}, document.title);
        }
    }, [state, askQuestion, isServiceReady]);

    const handleSendMessage = () => {
        if (isLoading || !isServiceReady || !query.trim()) return;

        const documentId = ddlValue.value?.id || getDefaultDocument().id;
        askQuestion(query, documentId, ISEnabled, IEnabled);
        setQuery('');
    };

    const handleQueryChange = (event: any) => {
        setQuery(event.value);
    };

    const handleDdlValueChange = (event: DropDownListChangeEvent) => {
        setDdlValue({ value: event.target.value });
    };

    const handleToggleISEnabled = () => {
        const newValue = !ISEnabled;
        if (newValue === true) {
            setISEnabled(true);
            return;
        }

        if (newValue === false && IEnabled === false) {
            alert('At least one data source must be enabled. Either Internal Data or Internet Search must be active.');
            return
        }
        else {
            setISEnabled(false);
        }
    };

    const handleToggleIEnabled = () => {
        const newValue = !IEnabled;
        if (newValue === true) {
            setIEnabled(true);
            return;
        }

        if (newValue === false && ISEnabled === false) {
            alert('At least one data source must be enabled. Either Internal Data or Internet Search must be active.');
            return;
        }
        else {
            setIEnabled(false);
        }
    };

    const onSpeechToTextResult = (event: SpeechToTextResultEvent) => {
        const { isFinal, alternatives } = event;
        if (isFinal && alternatives.length > 0) {
            setQuery(alternatives[0].transcript);
        }
    };

    if (serviceError) {
        return (
            <div className='chat-container'>
                <div className='service-error'>
                    <h3>Service Error</h3>
                    <p>{serviceError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className='chat-container'>
            <Chat
                messages={messages}
                authorId={1}
                className="k-m-auto"
                messageContextMenuActions={undefined}
                timestampVisibility='hidden'
                messageBox={() => <span></span>}
                receiverMessageSettings={{
                    messageTemplate: AIMessageTemplate,
                    messageContextMenuActions: [{ id: 'undefined', label: '' }],
                    messageWidthMode: 'full',
                    showAvatar: false,
                    showUsername: false
                }}
                style={{
                    border: 'none',
                    width: '100%',
                    height: 'calc(100vh - 250px)',
                    maxWidth: '100%',
                    backgroundColor: 'transparent'
                }}
            />
            <PromptBox
                ISEnabled={ISEnabled}
                handleToggleISEnabled={handleToggleISEnabled}
                IEnabled={IEnabled}
                handleToggleIEnabled={handleToggleIEnabled}
                query={query}
                handleQueryChange={handleQueryChange}
                docs={DOCUMENT_OPTIONS}
                ddlValue={ddlValue}
                handleDdlValueChange={handleDdlValueChange}
                handleSendButtonClick={handleSendMessage}
                onSpeechToTextResult={onSpeechToTextResult}
                isLoading={isLoading || !isServiceReady}
            />
        </div>
    );
};